// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import { Box, Flex, Switch, Text, Button } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { isNotNil, isNil } from 'helpers/isNotNil';
import { BendYbcGrid } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendYbcGrid';
import { useBendTypes } from 'modules/Shop/EvolveFabrication/BendCreator/useBendTypes';

import { useBendCreatorFormContext } from '../Context/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from '../Context/useBendCreatorValidationContext';
import type { FieldsComponentProps } from '../Navigation/BendCreatorStepFields';
import { BendDimensionInput } from './BendDimensionInput';
import { BendDimInputValidationMessage } from './BendDimInputValidationMessage';
import { BendMachineProfileSelect } from './BendMachineProfileSelect';
import { BendValidationStatus } from './BendValidationStatus';

export const BendDimensionInputs = ({ onChangeStep }: FieldsComponentProps) => {
  const {
    values: { bendType: currentBendType },
  } = useBendCreatorFormContext();

  const { fieldGetInputProps } = useBendCreatorValidationContext();

  const { bendTypes } = useBendTypes();
  const currentBendTypeData = useMemo(
    () => bendTypes?.find(({ name }) => name === currentBendType) ?? null,
    [currentBendType, bendTypes],
  );

  const isCustomBend = currentBendType === 'Custom';

  if (isNil(currentBendTypeData) && !isCustomBend) {
    return (
      <>
        <Text>Pick a bend type first.</Text>
        <Button onClick={() => onChangeStep(null)} variant="light">
          Pick a bend type
        </Button>
      </>
    );
  }

  return (
    <>
      {!isCustomBend && (
        <Flex>
          <AgGridStyleTooltip
            label="When enabled, the leg length of the bend will adjust to the maximum length available based on the material stock length"
            withArrow
            openDelay={100}
          >
            <Switch
              label={
                <Flex gap={6} align="center">
                  Maximize Leg Length
                  <EvolveIcon icon="Info" size="sm" inline color="dimmed" />
                </Flex>
              }
              {...fieldGetInputProps.maximizeLegLength()}
            />
          </AgGridStyleTooltip>
        </Flex>
      )}
      {isCustomBend ? (
        <BendYbcGrid />
      ) : isNotNil(currentBendTypeData) ? (
        currentBendTypeData.inputs.map((bendTypeInput) => (
          <Box key={bendTypeInput.name}>
            <BendDimensionInput
              bendTypeInput={bendTypeInput}
              isStockLengthField={currentBendTypeData.stockLengthKeyName === bendTypeInput.keyName}
            />
            <BendDimInputValidationMessage bendTypeInput={bendTypeInput} />
          </Box>
        ))
      ) : (
        <Text c="dimmed" fz="sm">
          Select a bend type.
        </Text>
      )}
      <BendMachineProfileSelect />
      <BendValidationStatus currentBendTypeData={currentBendTypeData} />
    </>
  );
};
